import axios, { type AxiosResponse } from 'axios';

// 统一 API 客户端（开发期通过 Vite proxy 转发到 9528）
export const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  // 数组查询参数序列化为重复 key（counterparty=A&counterparty=B）：
  // Express 5 默认 simple parser 不认 axios 默认的 `counterparty[]=` 括号语法，会静默丢弃筛选
  paramsSerializer: { indexes: null },
  headers: {
    'Content-Type': 'application/json',
    // 试点期默认租户；登录成功后以 token 为准（后端会用 tenantId 覆盖该头）
    'X-Tenant-Id': 'default',
  },
});

// ---- 登录态管理 ----

export interface CurrentUser {
  id: string;
  username: string;
  displayName: string;
  tenantId: string;
  role: string;
}

const TOKEN_KEY = 'zia_token';
const USER_KEY = 'zia_user';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): CurrentUser | null {
  const raw = localStorage.getItem(USER_KEY);
  try {
    return raw ? (JSON.parse(raw) as CurrentUser) : null;
  } catch {
    return null;
  }
}

export function setSession(token: string, user: CurrentUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

// 请求拦截：自动携带 Bearer token
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 统一响应解包：{ success, data, error }；blob/text（导出、打印）直接透传
api.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (body instanceof Blob || typeof body === 'string') {
      return response;
    }
    if (body && body.success === false) {
      return Promise.reject(new Error(body.error?.message || 'Request failed'));
    }
    return body.data;
  },
  (error) => {
    const errData = error.response?.data?.error;
    const wrapped: any = new Error(errData?.message || error.message);
    // 携带 HTTP 状态与业务错误码/附加数据，供调用方区分 409 冲突等需二次确认的场景
    wrapped.status = error.response?.status;
    wrapped.code = errData?.code;
    wrapped.data = errData?.data;
    return Promise.reject(wrapped);
  }
);

// ---- 类型定义（与后端 service 对齐）----

export interface Account {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  parentId?: string | null;
  direction: 'debit' | 'credit';
  isSystem: number;
  isActive: number;
  createdAt: string;
  updatedAt: string;
}

export interface JournalLine {
  id: string;
  journalId: string;
  accountCode: string;
  accountName?: string;
  debit: number;
  credit: number;
  description?: string | null;
}

export interface JournalEntry {
  id: string;
  reference: string;
  entryDate: string;
  description?: string;
  status: 'draft' | 'posted' | 'approved' | 'rejected';
  totalDebit: number;
  totalCredit: number;
  attachmentCount?: number;  // 原始凭证影像数
  reversesId?: string | null;
  reversedById?: string | null;
  counterparty?: string | null;
  lines: JournalLine[];
  createdAt: string;
}

export interface ClassifiedTransaction {
  id: string;
  transactionId: string;
  amount: number;
  transactionTime: string;
  counterparty: string;
  remark: string;
  businessType?: string;
  confidence: number;
  tier: 'high' | 'medium' | 'low';
  needConfirm: boolean;
  journalId?: string | null;
}

export interface AnalyzeResult {
  fileName: string;
  headers: string[];
  sampleRows: Record<string, string>[];
  allRows: Record<string, string>[];
  totalRows: number;
  detectedMapping: Record<string, string>;
  mappingSource: 'learned_template' | 'rule_engine' | 'ai';
  unmappedHeaders: string[];
  confidence: number;
}

/** 逐行预览结果（与后端 PreviewRow 对齐） */
export interface PreviewRow {
  index: number;
  transactionTime: string;
  amount: number | null;
  direction: 'in' | 'out' | null;
  counterparty: string;
  remark: string;
  transactionId: string;
  issues: string[];
  canImport: boolean;
}

export interface PreviewResult {
  rows: PreviewRow[];
  stats: { total: number; importable: number; flagged: number };
}

// ---- Accounts API ----

export const accountsApi = {
  list: (params?: { type?: string; page?: number; pageSize?: number }): Promise<{ accounts: Account[]; total: number }> =>
    api.get('/accounts', { params }),

  create: (data: { code: string; name: string; type: string; direction?: string }): Promise<Account> =>
    api.post('/accounts', data),

  update: (id: string, data: Partial<Account>): Promise<Account> =>
    api.put(`/accounts/${id}`, data),

  remove: (id: string): Promise<void> =>
    api.delete(`/accounts/${id}`),
};

// ---- 往来单位档案（客户/供应商主数据）----

/** ⑪ 未归属对手方文本（凭证仍挂文本但不匹配任何启用档案） */
export interface UnattributedText {
  text: string;
  entryCount: number;
  openAmount: number; // 往来科目净额（>0 应收向挂账 / <0 付款核销向）
}

export interface Party {
  id: string;
  tenantId: string;
  name: string;
  type: 'customer' | 'supplier' | 'both';
  contact?: string | null;
  phone?: string | null;
  email?: string | null;
  taxId?: string | null;
  note?: string | null;
  isActive: boolean;
  ownerUserId?: string | null;   // 8.4 数据归属：负责人员工（null=无主公开）
  ownerName?: string | null;     // 负责人显示名（列表直接展示）
  createdAt: string;
  updatedAt: string;
  aliases?: string[];  // 改名历史别名（旧凭证文本仍能据此富化联系方式）
}

/** 8.4 租户内数据可见范围（与 server/services/dataScope.ts 同构） */
export type ScopeMode = 'all' | 'mine' | 'owner:<userId>';
export interface ScopeInfo { mode: 'all' | 'mine' | 'owner'; limited: boolean }

/** 当前登录用户是否拥有全租户可见性（admin/senior） */
export function hasFullScope(): boolean {
  const u = getStoredUser();
  return !u || u.role === 'admin' || u.role === 'senior_accountant';
}

/** 8.4 成员清单（负责人下拉） */
export interface UserBrief {
  id: string;
  username: string;
  displayName: string;
  role: string;
  isActive: boolean;
  lastLogin: string | null;
}

/** 8.4.4 档案交接审计轨迹记录 */
export interface HandoverRecord {
  id: string;
  partyId: string;
  partyName: string;
  reason: string;
  createdAt: string;
  fromUserId: string | null;
  fromName: string | null;
  toUserId: string | null;
  toName: string | null;   // 转公开时为「公开池」
  operatorName: string | null;
  review: { verdict: string; note: string; operatorName: string | null; createdAt: string } | null;  // 8.4.9 最新复核批注（无则 null）
  reviewCount: number;  // 8.4.10 累计复核次数（历次批注全量计数，0=未复核）
}

/** 8.4.10 一条交接记录的历史复核批注（时间倒序） */
export interface HandoverReview {
  id: string;
  verdict: string;
  note: string;
  operatorName: string | null;
  createdAt: string;
}

/** 8.4.12 待处理复核汇总：pending=最新结论 flagged 的交接记录数，overdue=其中超 days 天未销案者 */
export interface PendingReviews {
  pending: number;
  overdue: number;
  days: number;
}

/** 8.4.8 离职交接向导：名下单个档案的未结核算预览 */
export interface HandoverPreviewParty {
  id: string;
  name: string;
  type: string;
  openAmount: number;
  openCount: number;
  overdueAmount: number;
  overdueCount: number;
}

/** 8.4.8 离职交接向导预览整体响应；8.4.13 叠加经手交接的未销案预警（可选字段，契约不变） */
export interface HandoverPreview {
  ownedCount: number;
  ownedParties: HandoverPreviewParty[];
  totals: { openAmount: number; openCount: number; overdueAmount: number; overdueCount: number };
  pendingReviews?: { pending: number; overdue: number; hours: number };
}

/** 8.4.14 离职预警下钻明细行（与 preview.pendingReviews 同口径） */
export interface PendingReviewDetailItem {
  handoverId: string;
  partyName: string;
  direction: 'out' | 'in' | string;
  reason: string;
  reviewerName: string | null;
  note: string;
  reviewCreatedAt: string;
  ageHours: number;
  isOverdue: boolean;
}

/** 8.4.18 复核时效轻报表 */
export interface ReviewMetrics {
  thresholdHours: number;
  pendingTotal: number;
  byReviewer: Array<{
    operatorId: string; reviewerName: string; reviewerUsername: string;
    totalFlagged: number; resolvedCount: number; avgHours: number | null;
    overdueResolved: number; stillPending: number;
  }>;
}

export const usersApi = {
  list: (): Promise<UserBrief[]> => api.get('/users'),
  create: (data: { username: string; password: string; displayName?: string; role: string }): Promise<UserBrief> =>
    api.post('/users', data),
  /** 8.4 团队视图：停用/启用成员（仅 admin）；停用时名下有档案须携 transferTo（'public' 或成员 id，8.4.3 交接） */
  setActive: (id: string, isActive: boolean, transferTo?: string): Promise<{ id: string; isActive: boolean; transferred?: number }> =>
    api.patch(`/users/${id}/active`, { isActive, ...(transferTo !== undefined ? { transferTo } : {}) }),
  /** 8.4.3 变更角色（仅 admin；不可改自己） */
  setRole: (id: string, role: string): Promise<{ id: string; role: string }> => api.patch(`/users/${id}/role`, { role }),
  /** 8.4.3 重置密码（仅 admin；下次登录生效） */
  resetPassword: (id: string, newPassword: string): Promise<{ id: string }> => api.post(`/users/${id}/reset-password`, { newPassword }),
  /** 8.4.4 交接审计轨迹（仅 admin）：可按成员/按档案/按复核结论（none/confirmed/corrected/flagged，8.4.11）过滤，8.4.7 加 from/to 时间范围与 offset 分页 */
  listHandovers: (opts?: { userId?: string; partyId?: string; from?: string; to?: string; review?: string; limit?: number; offset?: number }): Promise<HandoverRecord[]> =>
    api.get('/users/handovers', { params: { ...(opts?.userId ? { userId: opts.userId } : {}), ...(opts?.partyId ? { partyId: opts.partyId } : {}), ...(opts?.from ? { from: opts.from } : {}), ...(opts?.to ? { to: opts.to } : {}), ...(opts?.review ? { review: opts.review } : {}), ...(opts?.limit ? { limit: opts.limit } : {}), ...(opts?.offset ? { offset: opts.offset } : {}) } }),
  /** 8.4.7 导出交接审计 CSV（仅 admin）：携当前过滤条件（含 8.4.11 review），浏览器直接下载 */
  exportHandovers: async (opts?: { userId?: string; partyId?: string; from?: string; to?: string; review?: string }): Promise<void> => {
    const res = (await api.get('/users/handovers/export', {
      params: { ...(opts?.userId ? { userId: opts.userId } : {}), ...(opts?.partyId ? { partyId: opts.partyId } : {}), ...(opts?.from ? { from: opts.from } : {}), ...(opts?.to ? { to: opts.to } : {}), ...(opts?.review ? { review: opts.review } : {}) },
      responseType: 'blob',
    })) as unknown as AxiosResponse<Blob>;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `档案交接审计_${opts?.from || '全部'}${opts?.to ? '_' + opts.to : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
  /** 8.4.8 离职交接向导预览（仅 admin）：名下启用档案清单 + 未结/超期聚合，只读；8.4.13 可携 reviewHours 调整超期阈值（小时，回退 8） */
  handoverPreview: (id: string, reviewHours?: number): Promise<HandoverPreview> => api.get(`/users/${id}/handover-preview`, { params: reviewHours ? { reviewHours } : {} }),
  /** 8.4.14 离职预警下钻明细（仅 admin，懒加载）：该成员经手交接中最新结论 flagged 未销案的具体清单；hours 回退 8 */
  pendingReviewsDetail: (id: string, hours?: number): Promise<{ items: PendingReviewDetailItem[]; hours: number }> =>
    api.get(`/users/${id}/pending-reviews-detail`, { params: hours ? { hours } : {} }),
  /** 8.4.9 对一条交接审计记录追加复核批注（仅 admin；只增不改） */
  addHandoverReview: (handoverId: string, verdict: string, note: string): Promise<{ id: string; handoverId: string; verdict: string; note: string; createdAt: string }> =>
    api.post(`/users/handovers/${handoverId}/reviews`, { verdict, note }),
  /** 8.4.10 读一条交接记录的全部复核轨迹（仅 admin；时间倒序，最新在前） */
  listHandoverReviews: (handoverId: string): Promise<HandoverReview[]> =>
    api.get(`/users/handovers/${handoverId}/reviews`),
  /** 8.4.11 销案（仅 admin）：对最新结论为可疑待核的记录追加【销案】confirmed 批注；非 flagged 则 400 */
  resolveHandover: (handoverId: string, note: string): Promise<{ id: string; handoverId: string; verdict: string; note: string; createdAt: string }> =>
    api.post(`/users/handovers/${handoverId}/resolve`, { note }),
  /** 8.4.12 待处理复核汇总（仅 admin；只读）：pending=最新结论 flagged 的记录数，overdue=其中超 days 天未销案者 */
  pendingReviews: (days?: number): Promise<PendingReviews> =>
    api.get('/users/handovers/pending-reviews', { params: days ? { days } : {} }),
  /** 8.4.18 复核时效轻报表（仅 admin；只读）：按复核人维度聚合 flagged→销案时长分布 + 超期率 */
  reviewMetrics: (hours?: number): Promise<ReviewMetrics> =>
    api.get('/users/handovers/review-metrics', { params: hours ? { hours } : {} }),
};

export const partiesApi = {
  list: (params?: { type?: string; isActive?: boolean; keyword?: string; page?: number; pageSize?: number; scope?: string }): Promise<{ parties: Party[]; total: number; scopeInfo?: ScopeInfo }> =>
    api.get('/parties', { params }),

  /** 有效档案名称清单（凭证录入下拉建议） */
  names: (): Promise<string[]> => api.get('/parties/names'),

  /** 对手方文本→档案匹配（⑨ 归一提示；现名优先、曾用名回退，未命中 null） */
  match: (text: string): Promise<{ matchedBy: 'name' | 'alias'; party: { id: string; name: string; contact?: string | null; phone?: string | null } } | null> =>
    api.get('/parties/match', { params: { text } }),

  create: (data: { name: string; type?: string; contact?: string; phone?: string; email?: string; taxId?: string; note?: string; ownerUserId?: string | null }): Promise<Party> =>
    api.post('/parties', data),

  update: (id: string, data: Partial<Party> & { backfill?: boolean }): Promise<Party & { backfilled?: number }> =>
    api.put(`/parties/${id}`, data),

  /** 停用（软删）；force=true 跳过未结余额 409 拦截 */
  remove: (id: string, force?: boolean): Promise<void> =>
    api.delete(`/parties/${id}${force ? '?force=1' : ''}`),

  /** 手工补录曾用名/别名（返回含最新 aliases 的档案） */
  addAlias: (id: string, alias: string): Promise<Party> =>
    api.post(`/parties/${id}/aliases`, { alias }),

  /** 移除一条曾用名/别名 */
  removeAlias: (id: string, alias: string): Promise<Party> =>
    api.delete(`/parties/${id}/aliases`, { params: { alias } }),

  /** ⑪ 未归属对手方文本清单（凭证仍挂文本但不匹配任何启用档案） */
  unattributed: (): Promise<Array<{ text: string; entryCount: number; openAmount: number }>> =>
    api.get('/parties/unattributed'),

  /** ⑪ 挂别名并归一：登记曾用名后把该文本凭证改写为档案现名（同事务），返回含 normalized 计数 */
  attachAliasNormalize: (id: string, alias: string): Promise<Party & { normalized: number }> =>
    api.post(`/parties/${id}/aliases/normalize`, { alias }),
};

// ---- Journal API ----

export const journalApi = {
  list: (params?: { status?: string; startDate?: string; endDate?: string; keyword?: string; counterparty?: string | string[]; scope?: string; page?: number; pageSize?: number }): Promise<{ entries: JournalEntry[]; total: number; scopeInfo?: ScopeInfo }> =>
    api.get('/journal', { params }),

  create: (data: {
    entryDate: string;
    description?: string;
    counterparty?: string;
    lines: Array<{ accountCode: string; debit?: number; credit?: number; description?: string }>;
  }): Promise<JournalEntry> => api.post('/journal', data),

  post: (id: string): Promise<JournalEntry> =>
    api.post(`/journal/${id}/post`),

  approve: (id: string, approvedBy: string): Promise<JournalEntry> =>
    api.post(`/journal/${id}/approve`, { approvedBy }),

  void: (id: string, voidBy?: string, voidDate?: string): Promise<JournalEntry> =>
    api.post(`/journal/${id}/void`, { voidBy, voidDate }),

  remove: (id: string): Promise<void> =>
    api.delete(`/journal/${id}`),
};

// ---- 凭证附件（原始凭证影像） ----

export interface VoucherAttachment {
  id: string;
  journalId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedBy?: string;
  createdAt: string;
}

export const attachmentApi = {
  list: (entryId: string): Promise<VoucherAttachment[]> =>
    api.get(`/journal/${entryId}/attachments`),

  upload: (entryId: string, file: File): Promise<VoucherAttachment> => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/journal/${entryId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // 图片/PDF 需带 token 鉴权，<img src> 无法携头 → axios blob 下载后 objectURL 预览
  // （拦截器对 blob 透传整个 AxiosResponse，需取 .data）
  file: async (id: string): Promise<Blob> => {
    const res = await api.get(`/journal/attachments/${id}/file`, { responseType: 'blob' });
    return (res as unknown as { data: Blob }).data;
  },

  remove: (id: string): Promise<void> =>
    api.delete(`/journal/attachments/${id}`),
};

// ---- Import API ----

export const importApi = {
  analyze: (file: File): Promise<AnalyzeResult> => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/import/analyze', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  confirm: (data: {
    platform?: string;
    headers: string[];
    rows: Record<string, string>[];
    mapping: Record<string, string>;
  }): Promise<{
    importedCount: number;
    skippedCount: number;
    classification: { saved: number; duplicated: number; byTier: Record<string, number> } | null;
  }> => api.post('/import/confirm', data),

  preview: (data: {
    rows: Record<string, string>[];
    mapping: Record<string, string>;
  }): Promise<PreviewResult> => api.post('/import/preview', data),
};

// ---- WeChat classify API ----

export const wechatApi = {
  pending: (params?: { tier?: string; page?: number; pageSize?: number }): Promise<{ items: ClassifiedTransaction[]; total: number }> =>
    api.get('/wechat/pending', { params }),

  batchConfirm: (ids: string[], businessType?: string): Promise<{ confirmed: number; rejected: number }> =>
    api.post('/wechat/batch-confirm', { ids, businessType }),

  businessTypes: (): Promise<Array<{ id: string; name: string; color: string }>> =>
    api.get('/wechat/business-types'),

  createBusinessType: (name: string, color?: string): Promise<{ id: string }> =>
    api.post('/wechat/business-types', { name, color }),

  generateJournal: (ids?: string[]): Promise<{ generated: number; skipped: number; references: string[] }> =>
    api.post('/wechat/generate-journal', { ids }),
};

// ---- 企微监管 API ----

export interface CustomerSignal {
  id: string;
  sessionId: string;
  signalType: 'renewal' | 'upgrade' | 'churn_risk';
  confidence: number;
  snippet: string;
  suggestedAction: string;
  status: 'unread' | 'read' | 'actioned';
  actedBy?: string | null;
  createdAt: string;
}

export interface SessionSummary {
  id: string;
  staffUserId: string;
  customerUserId: string;
  messageCount: number;
  lastMessageAt: string;
  lastMessages: Array<{ sentAt: string; content: string }>;
}

export const monitorApi = {
  ingestMessage: (data: {
    sessionId?: string;
    staffUserId: string;
    customerUserId: string;
    content: string;
  }): Promise<{ sessionId: string; isNewSession: boolean; signals: CustomerSignal[]; sensitiveHits: string[] }> =>
    api.post('/monitor/messages', data),

  signals: (params?: { status?: string; signalType?: string }): Promise<{ items: CustomerSignal[]; total: number }> =>
    api.get('/monitor/signals', { params }),

  updateSignalStatus: (id: string, status: 'read' | 'actioned', actedBy?: string): Promise<CustomerSignal> =>
    api.post(`/monitor/signals/${id}/status`, { status, actedBy }),

  sessions: (): Promise<SessionSummary[]> =>
    api.get('/monitor/sessions'),
};

// ---- Auth API ----

export const authApi = {
  login: (username: string, password: string): Promise<{ token: string; user: CurrentUser }> =>
    api.post('/auth/login', { username, password }),

  changePassword: (oldPassword: string, newPassword: string): Promise<{ message: string }> =>
    api.post('/auth/change-password', { oldPassword, newPassword }),
};

// ---- Reports API ----

export interface TrialBalanceRow {
  accountCode: string;
  accountName: string;
  accountType: string;
  totalDebit: number;
  totalCredit: number;
  balance: number;
}

export interface IncomeStatement {
  period: string;
  revenues: Array<{ accountCode: string; accountName: string; amount: number }>;
  expenses: Array<{ accountCode: string; accountName: string; amount: number }>;
  totalRevenue: number;
  totalExpense: number;
  netProfit: number;
}

export interface CashSummary {
  period: string;
  accounts: Array<{ accountCode: string; accountName: string; inflow: number; outflow: number; netChange: number }>;
  totalNetChange: number;
  wechatInflow: number;
}

export interface LedgerRow {
  entryId: string;
  entryDate: string;
  reference: string;
  description: string | null;
  lineDescription: string | null;
  debit: number;
  credit: number;
  balance: number;
}

export interface AccountLedger {
  accountCode: string;
  accountName: string;
  accountType: string;
  period: string;
  openingBalance: number;
  rows: LedgerRow[];
  totalDebit: number;
  totalCredit: number;
  closingBalance: number;
}

export interface AccountComparisonRow {
  accountCode: string;
  accountName: string;
  accountType: string;
  openingBalance: number;
  totalDebit: number;
  totalCredit: number;
  closingBalance: number;
}

export interface BalanceSheetRow {
  accountCode: string;
  accountName: string;
  openingBalance: number;
  closingBalance: number;
}

export interface BalanceSheet {
  period: string;
  assets: BalanceSheetRow[];
  liabilities: BalanceSheetRow[];
  equity: BalanceSheetRow[];
  unclassified: BalanceSheetRow[];
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  retainedEarnings: { opening: number; closing: number };
  balanced: boolean;
  difference: number;
}

// ---- 账龄分析与财务预警 ----

export type AgingSide = 'receivable' | 'payable';

export interface AgingItem {
  accountCode: string;
  accountName: string;
  side: AgingSide;
  counterparty: string;
  journalId: string;
  reference: string;
  entryDate: string;
  description: string;
  openAmount: number;
  paidAmount: number;
  agingDays: number;
  bucket: string;
  overdue: boolean;
}

export interface AgingBucket {
  label: string;
  from: number;
  to: number;
  amount: number;
  count: number;
  ratio: number;
}

export interface CounterpartyStat {
  counterparty: string;
  amount: number;
  count: number;
  overdueAmount: number;
  overdueCount: number;
  maxDays: number;
  contact?: string | null;  // 往来单位档案富化（名称/曾用名匹配且启用时有值）
  phone?: string | null;
  partyId?: string | null;  // ⑩ 归户命中的档案 id
  texts?: string[];         // ⑩ 并入本行的全部凭证文本（现名+曾用名）
  merged?: boolean;         // ⑩ 多文本归户合并
}

export interface AgingGroup {
  side: AgingSide;
  label: string;
  total: number;
  count: number;
  avgDays: number;
  maxDays: number;
  overdueAmount: number;
  overdueCount: number;
  buckets: AgingBucket[];
  byCounterparty: CounterpartyStat[];
  items: AgingItem[];
}

export interface AgingReport {
  asOf: string;
  termsDays: number;
  groups: AgingGroup[];
  accounts: Array<{ code: string; name: string; side: AgingSide; open: number }>;
}

export interface FinanceAlert {
  id: string;
  dedupeKey: string;
  alertType: 'aging_overdue';
  side: AgingSide;
  accountCode?: string;
  journalId?: string;
  reference?: string;
  amount: number;
  agingDays: number;
  message: string;
  status: 'unread' | 'read' | 'actioned';
  actedBy?: string | null;
  actedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  // 催收线索（后端按凭证 counterparty → 往来单位档案动态关联）
  counterparty?: string | null;
  contact?: string | null;
  phone?: string | null;
}

export interface AgingScanResult {
  created: number;
  updated: number;
  resolved: number;
  /** 本轮命中的超期笔数（活跃预警口径，不含已销号历史） */
  overdueCount: number;
  overdueTotal: number;
  termsDays: number;
  alerts: FinanceAlert[];
}

/**
 * 拉取套打 HTML 并开新窗口（自动携带 token / 租户头，窗口内自动唤起打印）
 */
async function openPrintWindow(path: string, params?: Record<string, string | undefined>): Promise<void> {
  const res = (await api.get(path, {
    params: { ...params, operator: getStoredUser()?.username || getStoredUser()?.id || 'system' },
    responseType: 'text',
  })) as unknown as AxiosResponse<string>;
  const blob = new Blob([res.data], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (w) w.addEventListener('beforeunload', () => URL.revokeObjectURL(url));
}

export const reportsApi = {
  trialBalance: (period?: string): Promise<{ rows: TrialBalanceRow[]; sumDebit: number; sumCredit: number }> =>
    api.get('/reports/trial-balance', { params: { period } }),

  incomeStatement: (period?: string): Promise<IncomeStatement> =>
    api.get('/reports/income-statement', { params: { period } }),

  balanceSheet: (period?: string): Promise<BalanceSheet> =>
    api.get('/reports/balance-sheet', { params: { period } }),

  cashSummary: (period?: string): Promise<CashSummary> =>
    api.get('/reports/cash-summary', { params: { period } }),

  ledger: (accountCode: string, period?: string): Promise<AccountLedger> =>
    api.get('/reports/ledger', { params: { accountCode, period } }),

  comparison: (codes: string[], period?: string): Promise<{ period: string; rows: AccountComparisonRow[] }> =>
    api.get('/reports/comparison', { params: { codes: codes.join(','), period } }),

  /** 应收/应付账龄分析（8.4 可携 scope 圈定数据范围） */
  aging: (params?: { asOf?: string; termsDays?: number; scope?: string }): Promise<AgingReport & { scopeInfo?: ScopeInfo }> =>
    api.get('/reports/aging', { params }),

  /** 扫描超期账龄 → 生成/刷新财务预警 */
  agingScan: (body?: { asOf?: string; termsDays?: number }): Promise<AgingScanResult> =>
    api.post('/reports/aging/scan', body ?? {}),

  /** 财务预警列表 */
  agingAlerts: (params?: { status?: string; side?: string; scope?: string }): Promise<{ items: FinanceAlert[]; total: number; scopeInfo?: ScopeInfo }> =>
    api.get('/reports/aging/alerts', { params }),

  /** 预警处理状态 */
  agingAlertStatus: (id: string, status: 'read' | 'actioned', actedBy?: string): Promise<FinanceAlert> =>
    api.post(`/reports/aging/alerts/${id}/status`, { status, actedBy }),

  /** 导出催收单 CSV（携 token 拉取二进制流后触发下载，按当前 status/side 筛选；8.4 与页面同 scope） */
  agingAlertsExport: async (params?: { status?: string; side?: string; scope?: string }): Promise<void> => {
    const res = (await api.get('/reports/aging/alerts/export', {
      params,
      responseType: 'blob',
    })) as unknown as AxiosResponse<Blob>;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `催收预警单_${params?.status || '全部'}${params?.side ? '_' + params.side : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** 导出报表 Excel（携 token 拉取二进制流后触发浏览器下载） */
  exportXlsx: async (kind: 'trial-balance' | 'income-statement' | 'balance-sheet' | 'cash-summary', period?: string): Promise<void> => {
    const res = (await api.get(`/reports/export/${kind}.xlsx`, {
      params: { period },
      responseType: 'blob',
    })) as unknown as AxiosResponse<Blob>;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kind}_${period || 'all'}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** 导出科目明细账 */
  exportLedger: async (accountCode: string, period?: string): Promise<void> => {
    const res = (await api.get('/reports/export/ledger.xlsx', {
      params: { accountCode, period },
      responseType: 'blob',
    })) as unknown as AxiosResponse<Blob>;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `明细账_${accountCode}_${period || 'all'}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** 导出多科目对比账簿 */
  exportComparison: async (codes: string[], period?: string): Promise<void> => {
    const res = (await api.get('/reports/export/comparison.xlsx', {
      params: { codes: codes.join(','), period },
      responseType: 'blob',
    })) as unknown as AxiosResponse<Blob>;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `多科目对比账簿_${period || 'all'}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** 凭证打印版（经 axios 携 token/租户头拉取 HTML 后开新窗口，自动唤起打印） */
  printVoucher: (id: string): Promise<void> => openPrintWindow(`/reports/export/print-voucher/${id}`),

  /** 多张凭证连续套打（末尾自带原始附件清单页） */
  printVouchers: (ids: string[], opts?: { withAttachmentList?: boolean }): Promise<void> =>
    openPrintWindow('/reports/export/print-vouchers', {
      ids: ids.join(','),
      withAttachmentList: opts?.withAttachmentList === false ? '0' : '1',
    }),

  /** 明细账套打（页眉页脚 + 表头跳页重复） */
  printLedger: (accountCode: string, period?: string): Promise<void> =>
    openPrintWindow('/reports/export/print-ledger', { accountCode, period }),

  /** 往来账龄分析表导出 Excel */
  exportAging: async (params?: { asOf?: string; termsDays?: number; scope?: string }): Promise<void> => {
    const res = (await api.get('/reports/export/aging.xlsx', {
      params: { asOf: params?.asOf, termsDays: params?.termsDays, scope: params?.scope },
      responseType: 'blob',
    })) as unknown as AxiosResponse<Blob>;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `往来账龄表_${params?.asOf || 'all'}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** 往来账龄套打（携 token 拉 HTML 开新窗自动打印） */
  printAging: (params?: { asOf?: string; termsDays?: number; scope?: string }): Promise<void> =>
    openPrintWindow('/reports/export/print-aging', {
      asOf: params?.asOf,
      termsDays: params?.termsDays != null ? String(params.termsDays) : undefined,
      scope: params?.scope,
    }),
};

// ---- 租户运维 API（仅 admin）----

export interface TenantStats {
  users: number;
  accounts: number;
  journalEntries: number;
  postedEntries: number;
  draftEntries: number;
  journalLines: number;
  wechatTransactions: number;
  attachments: number;
  attachmentBytes: number;
  sessions: number;
  signals: number;
  unreadAlerts: number;
  closedPeriods: number;
}

export interface TenantQuotaUsage {
  pct: { journalEntries: number; accounts: number; users: number; attachmentBytes: number };
  nearLimit: boolean;
  nearest: string;
}

export interface TenantRow {
  id: string;
  name: string;
  createdAt: string;
  isDemo: boolean;
  stats: TenantStats;
  usage: TenantQuotaUsage;
}

export interface TenantListResult {
  tenants: TenantRow[];
  totals: { tenants: number; journalEntries: number; attachmentBytes: number };
  quotas: { maxJournalEntries: number; maxAccounts: number; maxUsers: number; maxAttachmentBytes: number };
  uploadRoot: string;
}

export interface DemoSeedResult {
  ok: boolean;
  exitCode: number;
  output: string[];
  durationMs: number;
}

export interface ArchiveRestoreResult {
  targetTenantId: string;
  sourceTenantId: string;
  inserted: Record<string, number>;
  skippedAccounts: number;
  usersSkipped: number;
  attachments: { total: number; filesPresent: number };
}

export const adminApi = {
  tenants: (): Promise<TenantListResult> => api.get('/admin/tenants'),

  createTenant: (id: string, name?: string): Promise<{ id: string; name: string; created: boolean }> =>
    api.post('/admin/tenants', { id, name }),

  /** 下载租户数据归档包（JSON） */
  archive: async (tenantId?: string): Promise<{ fileName: string; bytes: number }> => {
    const res = (await api.get('/admin/archive', {
      params: tenantId ? { tenantId } : undefined,
      responseType: 'blob',
    })) as unknown as AxiosResponse<Blob>;
    const disposition = (res.headers?.['content-disposition'] as string) || '';
    const fileName = (/filename="([^"]+)"/.exec(disposition) || [])[1] || `archive_${tenantId || 'default'}.json`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    return { fileName, bytes: res.data.size };
  },

  /** 重置 / 灌入演示数据（default 租户，幂等 seed） */
  resetDemo: (): Promise<DemoSeedResult> => api.post('/admin/demo/reset', {}),

  /** 归档包恢复到目标租户（只面向新租户/空租户；前端先解析文件做格式闸门，减少无效上传） */
  restoreArchive: async (targetTenantId: string, file: File): Promise<ArchiveRestoreResult> => {
    const text = await file.text();
    let bundle: any;
    try {
      bundle = JSON.parse(text);
    } catch {
      throw new Error('归档文件不是合法 JSON，请选择「⬇ 归档包」导出的原文件');
    }
    if (!bundle?.meta?.tenantId || !bundle?.data) {
      throw new Error('归档文件缺少 meta/data 结构，不是本系统导出的归档包');
    }
    return api.post('/admin/archive/restore', { targetTenantId, bundle });
  },
};

// ---- Dashboard API ----

export interface DashboardSummary {
  accounts: { total: number };
  journal: { draft: number; posted: number; approved: number };
  wechat: {
    pending: { high: number; medium: number; low: number };
    confirmedNoJournal: number;
    monthInflow: number;
  };
  signals: { unread: number; alertUnread: number };
  aging: {
    receivableTotal: number;
    payableTotal: number;
    receivableAvgDays: number;
    payableAvgDays: number;
    overdueCount: number;
    overdueAmount: number;
    maxDays: number;
  };
  month: {
    period: string;
    totalRevenue: number;
    totalExpense: number;
    netProfit: number;
    cashNetChange: number;
  };
  /** 客户切换器：当前选中的往来档案（未选中为 null；仅 aging 口径随其变化） */
  party: { id: string; name: string } | null;
  ai: { available: boolean; mode: 'hybrid' | 'rules_only' };
}

export const dashboardApi = {
  summary: (period?: string, opts?: { partyId?: string }): Promise<DashboardSummary> =>
    api.get('/dashboard/summary', { params: { period, partyId: opts?.partyId } }),
};

// ---- Closing (月结) API ----

export interface PeriodStatus {
  period: string;
  closed: boolean;
  closedAt?: string | null;
  closedBy?: string | null;
  closingJournalId?: string | null;
  closingReference?: string | null;
  draftCount: number;
  postedCount: number;
  totalRevenue: number;
  totalExpense: number;
  netProfit: number;
  canClose: boolean;
  blockReason?: string;
}

export interface ClosingRecord {
  period: string;
  status: 'closed' | 'reopened';
  closedAt: string | null;
  closedBy: string | null;
  closingReference: string | null;
}

export const closingApi = {
  status: (period?: string): Promise<PeriodStatus> =>
    api.get('/closing/status', { params: { period } }),

  periods: (): Promise<{ closings: ClosingRecord[]; activePeriods: string[] }> =>
    api.get('/closing/periods'),

  close: (period: string, closedBy?: string): Promise<{ period: string; closingReference: string | null; netProfit: number; transferredAccounts: number }> =>
    api.post('/closing/close', { period, closedBy }),

  reopen: (period: string): Promise<{ period: string; voidedReference: string | null }> =>
    api.post('/closing/reopen', { period }),

  yearStatus: (year: string): Promise<YearStatus> =>
    api.get('/closing/year-status', { params: { year } }),

  yearClose: (year: string, closedBy?: string): Promise<{ year: string; annualReference: string | null; netProfit: number; transferred: boolean }> =>
    api.post('/closing/year-close', { year, closedBy }),

  yearReopen: (year: string): Promise<{ year: string; voidedReference: string | null }> =>
    api.post('/closing/year-reopen', { year }),
};

export interface YearStatus {
  year: string;
  netProfit: number;
  openMonths: string[];
  alreadyTransferred: boolean;
  annualReference: string | null;
  annualClosedAt: string | null;
  canClose: boolean;
  blockReason?: string;
}
